import React, { Component } from 'react'
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import AnalyticsManager, { PageEventsTypes } from '@app/analytics/manager'

import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { createSelectorCreator, defaultMemoize } from 'reselect'
import { Helmet } from 'react-helmet'

import Loader from '@app/components/ui/placeholders/Loader'

import landingRoutes from '@app/routes/landingRoutes'
import LandingLayout from './landing'

import publicRoutes from '@app/routes/publicRoutes'
import PublicLayout from './public'
import RoutingError from './public/RoutingError'

import studentRoutes from '@app/routes/studentRoutes'
import StudentLayout from './students'

import teacherRoutes from '@app/routes/teacherRoutes'
import TeacherLayout from './teachers'

import parentRoutes from '@app/routes/parentRoutes'
import ParentLayout from './parents'

import _ from 'lodash'
import Cookies from 'js-cookie'

import * as sessionActions from '@app/actions/session'
import * as globalActions from '@app/actions/global'

import { afterSignInRedirect } from '@app/helpers/sessionHelper'
import { UserContext, createUserContext } from '@app/context/user-context'

import { honeybadger } from '@app/honeybadger'
import { isMobileApp } from "@app/helpers/isMobileApp";

const userSelector = state => state.session.user
const globalSelector = state => state.global

const equalSelector = createSelectorCreator(defaultMemoize, _.isEqual)

const templateSelector = equalSelector(
  [userSelector, globalSelector],
  (user, global) => { return { user, global } }
)

const mapStateToProps = (state) => templateSelector(state)

const mapDispatchToProps = (dispatch) => {
  return {
    boundSessionActions: bindActionCreators(sessionActions, dispatch),
    boundGlobalActions: bindActionCreators(globalActions, dispatch)
  }
}

const mapPublicRoutes = (router, global, Layout) => {
  return _.map(router, (route, key) => {
    const { component, routes, layout, ...rest } = route

    return (
      <Route key={key} {...rest}
        render={(route) => <Layout layout={layout} component={component} route={route} global={global} />}
      />
    )
  })
}

class Template extends Component {
  constructor(props) {
    super(props)

    this.handleVerification = this.handleVerification.bind(this)
    this.updateEventState = this.updateEventState.bind(this)
    this.fetchUser = this.fetchUser.bind(this)
    this.handleExternalLinks = this.handleExternalLinks.bind(this)
    this.handleResume = this.handleResume.bind(this)
    this.handlePageShow = this.handlePageShow.bind(this)
    this.handleJwtStateChange = this.handleJwtStateChange.bind(this)

    this.stateUpdateInterval = null
    this.userUpdateInterval = null

    this.state = { isInitialyVerified: false, resuming: false }
    this.lastJwtAuthenticated = undefined
  }

  handleExternalLinks(e) {
    const link = e.target.closest('a[data-external-link]');
    if (!link) return;

    const isMobile = isMobileApp();
    
    if (isMobile) {
      e.preventDefault();
      const action = link.dataset.action || 'unknownAction';
      const searchParams = new URLSearchParams(window.location.search);
      
      searchParams.set(action, link.href);
      window.history.pushState({}, '', `${window.location.pathname}?${searchParams.toString()}`);
      
      setTimeout(() => {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete(action);
        const newUrl = currentParams.toString() 
          ? `${window.location.pathname}?${currentParams.toString()}`
          : window.location.pathname;
        
        window.history.pushState({}, '', newUrl);
      }, 500);
    }
  }

  componentDidMount() {
    const that = this;

    document.addEventListener('click', this.handleExternalLinks);

    window.jwtProxy.push({
      load: function(p) {
        that.props.boundSessionActions.verify().then(that.handleVerification)
      }
    });

    window.jwtProxy.push({
      stateChange: this.handleJwtStateChange
    });

    window.addEventListener('visibilitychange', this.handleResume);
    window.addEventListener('focus', this.handleResume);
    window.addEventListener('pageshow', this.handlePageShow);

    const configName = '_brics_transition_started';
    const ms = Cookies.get(configName);

    if (ms) {
      try {
        const measure = new Date().getTime() - parseInt(ms);

        if (window.CLICKSTREAM_URL && window.CLICKSTREAM_URL !== "" && !isNaN(measure)) {
          fetch(window.CLICKSTREAM_URL, {
            method: 'POST',
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              subject: 'igp.app_metric',
              version: 1,
              payload: {
                app: 'uolo',
                metric: 'brics_transition',
                payload: JSON.stringify({
                  value: measure
                }),
                ip: '%%ip%%',
                user_agent: navigator.userAgent,
                entry: document.location.href
              }
            })
          });
        }
      }
      catch (error) {
        console.error(error);
      }

      Cookies.remove(
        configName, {
          path: '/',
          domain: location.hostname.split('.').reverse().splice(0, 2).reverse().join('.')
        }
      );
    }

    this.userUpdateInterval = setInterval(this.fetchUser, 60000)
    this.stateUpdateInterval = setInterval(this.updateEventState, 60000)
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleExternalLinks);
    window.removeEventListener('visibilitychange', this.handleResume);
    window.removeEventListener('focus', this.handleResume);
    window.removeEventListener('pageshow', this.handlePageShow);
    clearInterval(this.stateUpdateInterval)
    clearInterval(this.userUpdateInterval)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.global.locale !== this.props.global.locale) {
      this.forceUpdate()
    }
  }

  updateEventState() {
    this.props.boundGlobalActions.fetchState()
  }

  fetchUser() {
    this.props.boundSessionActions.fetchUserIfNeeded()
  }

  handleVerification() {
    const { user, history } = this.props

    if (user && history.location.pathname === '/') {
      afterSignInRedirect(user, history)
    }

    this.setState(() => { return { isInitialyVerified: true } })
  }

  render() {
    var user = this.props.user
    AnalyticsManager.user = user
    if (user) { user = user.transform }

    const { history, global } = this.props
    const { isInitialyVerified, resuming } = this.state

    if (!isInitialyVerified || resuming) { return <Loader /> }

    honeybadger.setContext({
      user_id: user && user.id,
      user_type: user && user.role,
      country: window.COUNTRY,
      pathname: window.location.pathname
    })

    AnalyticsManager.emitPageEvent(PageEventsTypes.OlympHomeAppear);

    return (
      <UserContext.Provider key={global.locale} value={createUserContext(user)}>
        <Helmet>
          <title>{I18n.t('landing.title')}</title>
          <meta name="description" content={I18n.t('landing.description')} />
          <meta name="viewport" content="width=device-width, user-scalable=yes, initial-scale=1" />
          <html className={`${I18n.locale} ${window.COUNTRY}`} />
        </Helmet>
        <ConnectedRouter history={history}>
          <Switch>
            {mapPublicRoutes(landingRoutes, global, LandingLayout)}
            {mapPublicRoutes(publicRoutes, global, PublicLayout)}

            {
              _.map(studentRoutes, (route, key) => {
                const { component, layout, ...rest } = route
                const StudentLayoutComponent = layout || StudentLayout;
                return (
                  <Route key={key} {...rest}
                    render={(route) =>
                      user && user.isStudent ? (
                        <StudentLayoutComponent
                          component={component} route={route}
                          student={user.asStudent}
                          global={global} />
                      ) : (
                        <Redirect to='/' />
                      )
                    }
                  />
                )
              })
            }

            {
              _.map(teacherRoutes, (route, key) => {
                const { component, layout, ...rest } = route;
                const TeacherLayoutComponent = layout || TeacherLayout;
                return (
                  <Route key={key} {...rest}
                    render={(route) =>
                      user && user.isTeacher ? (
                        <TeacherLayoutComponent
                          component={component} route={route}
                          teacher={user.asTeacher}
                          global={global} />
                      ) : (
                        <Redirect to='/' />
                      )
                    }
                  />
                )
              })
            }

            {
              _.map(parentRoutes, (route, key) => {
                const { component, ...rest } = route
                return (
                  <Route key={key} {...rest}
                    render={(route) =>
                      user && user.isParent ? (
                        <ParentLayout
                          component={component} route={route}
                          parent={user.asParent}
                          global={global} />
                      ) : (
                        <Redirect to='/' />
                      )
                    }
                  />
                )
              })
            }

            <Route component={RoutingError} />
          </Switch>
        </ConnectedRouter>
      </UserContext.Provider>
    )
  }

  async handleResume() {
    if (this.state.resuming) { return }
    if (document.visibilityState && document.visibilityState !== 'visible') { return }
    if (!this._shouldAttemptResume()) { return }

    this.setState({ resuming: true })

    try {
      if (window.jwtProxy && typeof window.jwtProxy.reload === 'function') {
        try {
          window.jwtProxy.reload()
        } catch (e) {}

        const start = Date.now()
        const timeoutMs = 4000

        await new Promise((resolve) => {
          const check = () => {
            const authenticated = !!(window.jwtProxy && window.jwtProxy.state && window.jwtProxy.state.authenticated)
            if (authenticated || Date.now() - start >= timeoutMs) { return resolve() }
            setTimeout(check, 100)
          }
          check()
        })
      }

      await this.props.boundSessionActions.verify()
    } catch (e) {
      // no-op
    } finally {
      this.setState({ resuming: false }, this.handleVerification)
    }
  }

  _shouldAttemptResume() {
    if (!this.props.user) { return false }
    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) { return false }

    const jp = window.jwtProxy
    if (!jp || !jp.state) { return false }

    const { authenticated, token } = jp.state

    if (!authenticated) { return true }
    if (token && token.exp instanceof Date) {
      const remainingMs = token.exp - new Date()
      const THRESHOLD = 60000
      return remainingMs <= THRESHOLD
    }

    return false
  }

  handlePageShow(e) {
    try {
      const isPersisted = e && e.persisted === true
      let isBackForward = false
      if (typeof performance !== 'undefined' && performance && typeof performance.getEntriesByType === 'function') {
        const nav = performance.getEntriesByType('navigation')
        if (nav && nav.length > 0) {
          isBackForward = nav[0].type === 'back_forward'
        }
      }

      if ((isPersisted || isBackForward) && (!document.visibilityState || document.visibilityState === 'visible')) {
        this.handleResume()
      }
    } catch (err) {
      // no-op
    }
  }

  handleJwtStateChange(jp) {
    try {
      const now = jp && jp.state && jp.state.authenticated === true
      const was = this.lastJwtAuthenticated
      this.lastJwtAuthenticated = now

      if (was === false && now === true) {
        const startResume = () => this.setState({ resuming: true })
        const stopResume = () => this.setState({ resuming: false }, this.handleVerification)

        startResume()
        this.props.boundSessionActions.verify().finally(stopResume)
      }
    } catch (e) {
      // no-op
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Template)
